import * as React from 'react';
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {captureError} from '../../integrations';
import {DD_PRIVACY_HIDDEN} from '../../util/datadog/privacy';
import {labelComponent, makeCaptions} from '../../util/media';
import {type LoadedMediaCardProps, type MediaCardProps} from '../MediaCard';
import MessageMediaNotFound from '../MessageMediaNotFound';
import {SingleRunLink} from '../MultiRunWorkspace/SingleRunMode';
import {useGetTrackingCallbacks} from '../PanelMediaBrowser/MediaBrowserPanelTileTrackingContext';
import {useMediaBrowserModalContext} from '../PanelMediaBrowser/modal/MediaBrowserModalContext';
import {InstrumentedCenteredWaveLoader as Loader} from './../../components/utility/InstrumentedLoader';
import {BoundingBoxes} from './BoundingBoxes';
import Image from './Image';
import {MasksOverlay} from './MasksOverlay';
import {resolveMediaSize} from './resolveMediaSize';
import {useImageCardMedia} from './useImageCardMedia';
import {getBBoxData, getMaskData} from './utils';

type ImageCardProps = MediaCardProps;

const ImageCardComp = (props: ImageCardProps) => {
  const {width, height, mediaIndex, mediaKey, globalStep, tileMedia, loading} =
    props;
  if (tileMedia == null) {
    return (
      <div data-test="image-card-wrapper" style={{height: '100%'}}>
        <div
          data-test="image-card-comp"
          className="image-card"
          style={{width, height, display: 'flex'}}>
          {loading ? (
            <Loader name="panel-content-loader" size="small" />
          ) : (
            <MessageMediaNotFound
              basic
              mediaIndex={mediaIndex}
              mediaKey={mediaKey}
              stepIndex={globalStep}
              mediaType="images"
            />
          )}
        </div>
      </div>
    );
  }

  return <LoadedImageCard {...props} tileMedia={tileMedia} />;
};
export const ImageCard = React.memo(ImageCardComp);
export default ImageCard;

function LoadedImageCard(props: LoadedMediaCardProps) {
  const image = useImageCardMedia(props);
  return <ImageCardInner {...props} image={image} />;
}

type ImageCardInnerProps = LoadedMediaCardProps & {
  image: ReturnType<typeof useImageCardMedia>;
};

export const ImageCardInner: React.FunctionComponent<ImageCardInnerProps> =
  React.memo(function ImageCardInnerComp(props) {
    const {
      run,
      customRunName,
      globalStep,
      width,
      height,
      mediaKey,
      mediaIndex,
      runSignature,
      maskOptions,
      controls,
      mediaPanelRef,
      actualSize,
      tileMedia,
      getMediaTracking,
      tileMediaSpec,
      invalidateAndRefetch,
      omitRunLabel,
      isWithinMediaBrowserModal,
      omitCaptions,
      mediaWidth,
      mediaHeight,
      onImageRender,
      image,
    } = props;
    const {step, objectURL, internalURL, filePath, thumbnails} = tileMedia;
    const {mediaMetadata, isSprite, naturalDimensions, imgRef, mediaSize} =
      image;

    const numLabels = props.labels?.length ?? 0;
    const includeRunLabel = !omitRunLabel && numLabels > 0;

    const {trackSuccess, trackError} = useGetTrackingCallbacks(
      tileMedia,
      tileMediaSpec,
      getMediaTracking
    );

    const handleImageError = useCallback(() => {
      trackError();
      invalidateAndRefetch?.();
    }, [trackError, invalidateAndRefetch]);

    const handleImageLoad = useCallback(
      (img: HTMLImageElement) => {
        if (onImageRender == null) {
          return;
        }
        // naturalDimensions state has not updated yet, so read the element.
        const size = resolveMediaSize({
          isSprite,
          thumbnails,
          naturalDimensions:
            img.naturalWidth > 0 && img.naturalHeight > 0
              ? {width: img.naturalWidth, height: img.naturalHeight}
              : null,
          mediaWidth,
          mediaHeight,
        });
        if (size != null) {
          onImageRender(size);
        }
      },
      [onImageRender, isSprite, thumbnails, mediaWidth, mediaHeight]
    );

    let imageURL = objectURL;
    if (internalURL) {
      try {
        // Encode the URL and fix any images with a # in the name as `#` is a valid URI component but not
        // for images being rendered as they will not link to any portion of the page.
        const url = new URL(encodeURI(internalURL).replace(/#/g, '%23'));
        // The backend ignores the legacy height/width sizing params; drop
        // them so the URL stays stable across panel resizes.
        url.searchParams.delete('height');
        url.searchParams.delete('width');
        imageURL = url.toString();
      } catch (err) {
        // if anything goes wrong here we'll log it but we've got the blob URL as fallback
        captureError(err, 'ImageCard');
      }
    }

    const {openModal} = useMediaBrowserModalContext();

    const masksRef = useRef<HTMLDivElement | null>(null);
    const boxesRef = useRef<HTMLDivElement | null>(null);

    // consider removing state since actualImageSize is not used
    // eslint-disable-next-line no-unused-vars
    const [actualImageSize, setActualImageSize] = useState<{
      width: number;
      height: number;
    } | null>({width, height});

    // Measure the actual rendered size of the image element (not natural dimensions)
    // This accounts for CSS object-fit: contain and container constraints
    useEffect(() => {
      const imgEl = imgRef.current;
      if (!imgEl) {
        return;
      }

      const updateSize = () => {
        const rect = imgEl.getBoundingClientRect();
        // assume the default/current size if the image is not visible/loaded
        if (rect.width && rect.height) {
          setActualImageSize({
            width: rect.width,
            height: rect.height,
          });
        }
      };

      // Initial measurement
      updateSize();

      // Set up ResizeObserver to update when image size changes
      const resizeObserver = new ResizeObserver(updateSize);
      resizeObserver.observe(imgEl);

      // Also listen for image load events in case dimensions change
      imgEl.addEventListener('load', updateSize);

      return () => {
        resizeObserver.disconnect();
        imgEl.removeEventListener('load', updateSize);
      };
      // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [naturalDimensions]); // Re-run when image loads (naturalDimensions becomes available)

    const rolledBack = globalStep !== step;

    // Compute pixel offset for sprite based images
    //
    // This will be scaled to the card and used for display
    // to calculate to offset.
    const spritePixelOffset = isSprite ? mediaIndex * width : 0;

    // Compute the pixel offset for the original sprite
    // This is used for downloading
    const originalWidth = mediaMetadata?.width ?? null;
    const originalPixelOffset =
      isSprite && originalWidth != null ? mediaIndex * originalWidth : 0;

    const getWBFileDataParams = useMemo(
      () => ({
        currentMediaMetadata: mediaMetadata,
        mediaIndex,
        mediaKey,
        run,
      }),
      [mediaMetadata, mediaIndex, mediaKey, run]
    );
    const maskData = useMemo(
      () => getMaskData(getWBFileDataParams),
      [getWBFileDataParams]
    );
    const boundingBoxData = useMemo(
      () => getBBoxData(getWBFileDataParams),
      [getWBFileDataParams]
    );

    const showImage = !maskOptions || !!maskOptions?.showImage;

    const imgStyle: CSSProperties = {
      left: -(actualSize ? originalPixelOffset : spritePixelOffset),
      height: '100%',
      display: showImage ? 'initial' : 'none',
      maxWidth: 'none',
      ...(!isSprite ? {width: '100%', objectFit: 'contain'} : {}),
    };

    const title = customRunName ?? run.displayName ?? runSignature.runName;
    const titleLink = <SingleRunLink {...runSignature} linkText={title} />;

    // Get captions if not omitted (captions are rendered in TileFooterSlot via CaptionSlot in new layout)
    // Default omitCaptions to true to prevent double-rendering when using new layout system
    const captions = omitCaptions
      ? []
      : makeCaptions(mediaMetadata, mediaIndex);
    // Need to add 16px to the height to account for the label so the image doesn't shrink when the label is visible
    // const heightWithLabel = includeRunLabel ? height + 16 * numLabels : height;

    const imageCardRef = useRef<HTMLDivElement>(null);
    // We need to know the actual size of the image card rendered so that we can use it to calculate the bounding box positions.
    // It may be less than the width/height passed in if there are captions taking up space on the bottom of the card.
    const cardSize = useMemo(
      () => ({
        width: imageCardRef.current?.clientWidth ?? width,
        height: imageCardRef.current?.clientHeight ?? height,
      }),
      [
        width,
        height,
        // These values are dependencies because we need to re-calculate every time the card is resized.
        imageCardRef?.current?.clientWidth,
        imageCardRef?.current?.clientHeight,
      ]
    );

    const cardJSX = (
      <div
        className="ui card image-card content-card"
        data-test="image-content-card"
        data-mask-options={maskOptions?.maskKeys.join(',')}
        style={{width, height}}>
        <div
          ref={imageCardRef}
          className="image-card-image hover:opacity-[0.88] w-full">
          <Image
            width={width}
            step={step}
            imgRef={imgRef}
            imageURL={imageURL}
            imgStyle={imgStyle}
            trackSuccess={trackSuccess}
            trackError={handleImageError}
            onImageLoad={handleImageLoad}
            rolledBack={rolledBack}
            globalStep={globalStep}
            filePath={filePath}
            originalWidth={originalWidth}
            originalPixelOffset={originalPixelOffset}
            runSignature={runSignature}
            thumbnails={
              // We should always show the original image if the user has selected "Use original size" or "Fit to available space",
              props.config.actualSize === true ||
              props.config.fitToDimension === true ||
              props.config.alwaysOriginal === true ||
              isWithinMediaBrowserModal // if the user has clicked the image to open the modal, then they want to look at it at full size
                ? undefined
                : thumbnails
            }
          />

          {mediaSize && maskData.length > 0 && (
            <MasksOverlay
              masksRef={masksRef}
              mediaKey={mediaKey}
              maskData={maskData}
              maskOptions={maskOptions}
              controls={controls?.segmentationMaskControl}
              mediaSize={mediaSize}
              runSignature={runSignature}
              mediaPanelRef={mediaPanelRef}
            />
          )}
          {mediaSize && boundingBoxData.length > 0 && (
            <div
              ref={boxesRef}
              className="bounding-boxes__container"
              data-dd-privacy={DD_PRIVACY_HIDDEN}>
              {boundingBoxData.map(({key, wbFile}) => (
                <BoundingBoxes
                  style={{position: 'absolute', top: 0, left: 0}}
                  key={key}
                  mediaKey={mediaKey}
                  boxStyle={
                    controls?.boundingBoxControl?.styles?.[mediaKey]?.[key]
                  }
                  boxToggles={
                    controls?.boundingBoxControl?.toggles?.[mediaKey]?.[key]
                  }
                  boxSliders={controls?.boundingBoxControl?.sliders}
                  boxKey={key}
                  runSignature={runSignature}
                  boxFileInfo={wbFile}
                  cardSize={cardSize}
                  mediaSize={mediaSize}
                  mediaPanelRef={mediaPanelRef}
                />
              ))}
            </div>
          )}
        </div>
        {/* CAPTIONS - Only render if not omitted (new layout renders in TileFooterSlot) */}
        {captions.length > 0 && (
          <div className="image-card-caption break-words">{captions}</div>
        )}
      </div>
    );

    const enableFullscreen = showImage;
    if (!enableFullscreen) {
      return cardJSX;
    }

    // Handle image click to open modal
    const handleImageClick = () => {
      if (!tileMediaSpec) {
        return;
      }
      openModal({
        mediaPanelRef,
        tileMediaSpec: tileMediaSpec,
        labels: props.labels || [],
        imageURL,
        originalPixelOffset,
        runSignature,
        fullScreenTitle: title,
        imageDimensions: mediaSize,
        boundingBoxData,
        mediaKey,
        controls: controls ?? {},
      });
    };
    return (
      <div onClick={handleImageClick}>
        {includeRunLabel && (
          <div
            className="h-16 overflow-hidden flex flex-col justify-stretch items-stretch shadow-none"
            style={{width}}>
            {labelComponent(props, step, titleLink)}
          </div>
        )}
        {cardJSX}
      </div>
    );
  });
